import React, { forwardRef } from 'react';

import EditionsIcon from '@atlaskit/icon-lab/core/editions';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Box, Inline, Pressable, xcss } from '@atlaskit/primitives';

import { type DiscoveryCTAButtonProps } from './types';

const baseStyles = xcss({
	display: 'inline-flex',
	paddingRight: 'space.150',
	paddingLeft: 'space.100',
	paddingTop: 'space.0',
	paddingBottom: 'space.0',
	textDecoration: 'none',
	borderRadius: 'radius.small',
	justifyContent: 'center',
	alignItems: 'center',
	width: 'fit-content',
	transition: 'background 0.1s ease-out',
	whiteSpace: 'nowrap',
});

const defaultSpacingStyles = xcss({
	minHeight: 'size.300',
});
const compactSpacingStyles = xcss({
	minHeight: 'size.200',
});

const defaultStyles = xcss({
	color: 'color.text.inverse',
	backgroundColor: 'color.background.accent.purple.bolder',
	':hover': {
		backgroundColor: 'color.background.accent.purple.bolder.hovered',
		textDecoration: 'none',
		color: 'color.text.inverse',
	},
	':active': {
		backgroundColor: 'color.background.accent.purple.bolder.pressed',
		textDecoration: 'none',
		color: 'color.text.inverse',
	},
});

const invertedStyles = xcss({
	color: 'color.text.discovery',
	backgroundColor: 'elevation.surface',
	borderWidth: 'border.width',
	borderStyle: 'solid',
	borderColor: 'color.border.accent.purple',
	':hover': {
		backgroundColor: 'color.background.accent.purple.subtlest.hovered',
		textDecoration: 'none',
		color: 'color.text.discovery',
	},
	':active': {
		backgroundColor: 'color.background.accent.purple.subtlest.pressed',
		textDecoration: 'none',
		color: 'color.text.discovery',
	},
});

const subtleStyles = xcss({
	backgroundColor: 'color.background.neutral.subtle',
	color: 'color.text.accent.purple',
	borderRadius: 'radius.small',
	whiteSpace: 'nowrap',
	':hover': {
		backgroundColor: 'color.background.accent.purple.subtlest.hovered',
	},
	':active': {
		backgroundColor: 'color.background.accent.purple.subtlest.pressed',
	},
});

const contentContainerStyles = xcss({
	display: 'flex',
	alignItems: 'center',
	gap: 'space.050',
});

const iconContainerStyles = xcss({
	display: 'flex',
	width: 'size.200',
	height: 'size.200',
	justifyContent: 'center',
	alignItems: 'center',
});

export const DiscoveryCTAButton: React.ForwardRefExoticComponent<
	Omit<DiscoveryCTAButtonProps, 'ref'> & React.RefAttributes<HTMLButtonElement>
> = forwardRef<HTMLButtonElement, DiscoveryCTAButtonProps>(
	({ buttonStyle, children, spacing, ...buttonProps }, ref) => {
		return (
			<Pressable
				ref={ref}
				{...buttonProps}
				xcss={[
					baseStyles,
					buttonStyle === 'default' && defaultStyles,
					buttonStyle === 'inverted' && invertedStyles,
					buttonStyle === 'subtle' && subtleStyles,
					spacing === 'compact' ? compactSpacingStyles : defaultSpacingStyles,
				]}
			>
				<Box xcss={contentContainerStyles}>
					<Box xcss={iconContainerStyles}>
						<EditionsIcon label="" />
					</Box>
					<Inline>{children}</Inline>
				</Box>
			</Pressable>
		);
	},
);
